import React, { useState } from 'react';
import { KeyRound, Loader2, Lock, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { myPayApi, myPayErr } from '@/services/myPayApi';

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '');

/** HR lookup: the staff member's My Pay PIN state + Reset PIN (HR / Admin only). */
export const PinStatus = ({ staff, pin, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try { onChange(await myPayApi.pinReset(staff.staff_id)); toast.success(`PIN cleared — ${staff.staff_name} sets a new one at the next login`); setOpen(false); } catch (e) { toast.error(myPayErr(e, 'Could not reset the PIN')); } finally { setBusy(false); }
  };
  const label = !pin.set ? 'Not set yet — set at the next login' : pin.locked ? 'Locked (too many wrong tries)' : `Set on ${when(pin.set_at)}`;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#E5DFD0] bg-white px-4 py-3 print:hidden" data-testid="hr-pin-status">
      {pin.locked ? <Lock className="h-4 w-4 text-red-600" /> : <KeyRound className="h-4 w-4 text-[#2D5F4F]" />}
      <span className="text-sm"><b>My Pay PIN:</b> <span className={pin.locked ? 'text-red-700 font-semibold' : 'text-gray-700'} data-testid="hr-pin-state">{label}</span></span>
      {pin.set && !pin.locked && pin.wrong_tries > 0 && <span className="text-xs text-amber-700" data-testid="hr-pin-wrong-tries">{pin.wrong_tries} wrong tr{pin.wrong_tries === 1 ? 'y' : 'ies'}</span>}
      {pin.last_reset && <span className="text-xs text-gray-500" data-testid="hr-pin-last-reset">Last reset {when(pin.last_reset.at)} by {pin.last_reset.by_name || '—'}</span>}
      {pin.can_reset && pin.set && (
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="ml-auto" data-testid="hr-pin-reset-btn"><RotateCcw className="h-4 w-4 mr-1" />Reset PIN</Button>
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="hr-pin-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset {staff.staff_name}'s PIN?</AlertDialogTitle>
            <AlertDialogDescription>Their old PIN stops working and any open My Pay session is closed. At the next login they enter Staff ID + date of birth and choose a new PIN.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="hr-pin-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); reset(); }} disabled={busy} className="bg-[#1A1F2E] hover:bg-[#2D5F4F]" data-testid="hr-pin-reset-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset PIN'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PinStatus;
